import React, { FC, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "~/components/Button";
import { CloseThin } from "~/components/Icons/fill";
import { ResumeModalStyled, BadgeStyled } from "./ResumeModalStyled";
import { ResumeQuoteResponse } from "~/types/products";
import { setInstallationOptions, setResume, setTracking } from "~/redux/actions/common";
import { FuelContext } from "~/providers/FuelProvider";
import { PAGE_ROUTES } from "~/constants/pages";
import { updateAddress } from "~/redux/actions/address";
import useFetch from "~/hooks/useFetch";
import { trackEvent } from "~/helpers/tracking";
import { setNewRelicCustomAttribute } from "~/helpers/newrelic";
import { setCookie } from "~/helpers/cookie";
import { useMonarchSlot } from "@redventures/cohesion-utils-react";
import {
  CartConfigAddressCheck,
  cartConfigAddressCheckDefaultValue,
} from "../../../../../monarch/Config | Address Check/default";
import Tooltip from "~/components/Tooltip";
import { InfoCircle } from "~/components/Icons/outline";
import setHtml from "~/helpers/setHtml";
import ResumeAddress from "./Address";
import { useModal } from "~/hooks/useModal";
import { buildReturnVisitorHeaders } from "~/helpers/offers";
import useReturnVisitorOfferEligibility from "~/hooks/useReturnVisitorOfferEligibility";
import { ReduxState } from "~/types/redux";
import { iconMap } from "~/constants/iconMap";
import { theme } from "~/styles";
import getColor from "~/helpers/getColor";

interface Props {
  handleSubmitCommercialModal?: () => void;
  address?: string;
  visible: boolean;
  apiVersion: string;
}

const ResumeModal: FC<Props> = ({ visible = true, apiVersion }) => {
  const [loading, setloading] = useState(true);
  const { router } = useContext(FuelContext);
  const { closeModal } = useModal();
  const { address } = useSelector((store: ReduxState) => store);

  const metadata = useMonarchSlot<CartConfigAddressCheck>("addressCheck")?.data;
  const resumeModal = metadata?.resumeModal || cartConfigAddressCheckDefaultValue.resumeModal;

  const dispatch = useDispatch();

  const returnVisitorOfferEligibility = useReturnVisitorOfferEligibility();
  const [isMonarchRVOStable, setIsMonarchRVOStable] = useState(false);
  const techAvailable = address?.techAvailable;
  const isCopper = techAvailable === "COPPER";

  const headers: HeadersInit = buildReturnVisitorHeaders(returnVisitorOfferEligibility, isCopper);

  const resumeQuote = useFetch<ResumeQuoteResponse>(`${apiVersion}/quote/resume`, { method: "GET", headers }, true);

  const handleCancel = () => {
    // Set declined state BEFORE closing modal to prevent reopening
    // Keep this session storage flag for immediate use and cross-page persistence
    // It ensures users who declined don't see the modal again, even after page navigation
    window.sessionStorage.setItem("declinedResume", "1");

    // Also track in Redux state
    dispatch(setResume("acceptedResume", false));
    dispatch(setResume("enabled", false));
    dispatch(setResume("completed", false));
    window.sessionStorage.removeItem("resumeModalActive");

    // Close modal after setting declined state
    closeModal();
    router.replace(PAGE_ROUTES.ADDRESS_CHECK);
    return;
  };

  const handleContinue = async () => {
    trackEvent({
      action: "elementClicked",
      data: {
        elementType: "MODAL",
        location: "resume_modal",
        text: resumeModal?.button?.copy ?? "",
      },
    });

    dispatch(setResume("acceptedResume", true));
    dispatch(setResume("enabled", true));
    dispatch(setResume("viewedModal", true));
    dispatch(setResume("completed", false));
    setCookie("resumeEnabled", true, { otGroup: "C0003" });

    // Keep this for landing pages - it's used to track resume across page navigations
    // Redux state won't persist across page loads, but sessionStorage will
    window.sessionStorage.setItem("seenResumeModal", "true");

    router.replace(PAGE_ROUTES.RESUME);
    window.sessionStorage.removeItem("resumeModalActive");
  };

  useEffect(() => {
    const rvoTimeout = setTimeout(() => {
      setIsMonarchRVOStable(true);
    }, 500);
    return () => clearTimeout(rvoTimeout);
  }, [returnVisitorOfferEligibility]);

  useEffect(() => {
    // Only make API calls if the modal is actually visible
    if (!visible) return;

    if (!isMonarchRVOStable) return;

    // Set viewedModal flag in redux only
    dispatch(setResume("viewedModal", true));

    const resumeStarted = Date.now();
    (async () => {
      const data: ResumeQuoteResponse = await resumeQuote.fetch();

      if (data?.success) {
        window.sessionStorage.setItem("resumeResponse", JSON.stringify(data));
        dispatch(setResume("sweetenerSelected", data?.sweetenerSelected));
        dispatch(setResume("creditChecked", data?.creditChecked));
        dispatch(setResume("cartPage", data?.cartPage));
        dispatch(setTracking("accountUuid", data?.accountUuid));
        dispatch(setTracking("quoteNumber", data?.quoteNumber));
        dispatch(updateAddress("install", data?.serviceAddress));
        // Installation before credit: force self install to false
        dispatch(setInstallationOptions("availableInstallOptions", data?.installationOptions));
        if (data?.rvoQuoteId) {
          dispatch(setTracking("quoteId", data?.quoteId));
        }
        setNewRelicCustomAttribute([{ name: "isResume", value: "true" }]);
        const timeToFetch = Date.now() - resumeStarted;
        setloading(false);
        //Tracks time taken for the address to load
        trackEvent({
          action: "elementViewed",
          data: {
            elementType: "MODAL",
            location: "resume_modal",
            text: `Time taken for address render: ${timeToFetch}`,
          },
        });
      } else {
        closeModal();
        window.sessionStorage.removeItem("resumeModalActive");
        router.replace(PAGE_ROUTES.ADDRESS_CHECK);
      }
    })();
  }, [visible, isMonarchRVOStable]);

  if (!visible) return null;

  return (
    <ResumeModalStyled>
      <CloseThin onClick={handleCancel} data-testid="modal-btn-close" />
      <div className="resume__modal">
        {resumeModal?.badge && (
          <ResumeModalBadge
            backgroundColor={resumeModal.badge.backgroundColor}
            borderColor={resumeModal.badge.borderColor}
            icon={resumeModal.badge.icon}
            iconColor={resumeModal.badge.iconColor}
            copyColor={resumeModal.badge.copyColor}
            copy={resumeModal.badge.copy}
          />
        )}
        {resumeModal?.heading && (
          <header className="resume__header">
            <p className="resume__title">{resumeModal?.heading.copy}</p>
          </header>
        )}
        <div className="resume__container" data-testid="resume__container">
          {resumeModal?.subheading && (
            <div>
              <span {...setHtml(resumeModal?.subheading.copy)} />
              {resumeModal?.tooltip && (
                <Tooltip
                  position="top"
                  theme="light"
                  html={
                    <div className="tooltip-info">
                      <span {...setHtml(resumeModal?.tooltip?.copy)} />
                    </div>
                  }
                >
                  <InfoCircle width={16} />
                </Tooltip>
              )}
            </div>
          )}
          <ResumeAddress />
          {resumeModal?.button && resumeModal?.button.copy && (
            <div className="resume__button-container">
              <Button
                disabled={loading || !isMonarchRVOStable}
                className="resume__submit"
                onClick={handleContinue}
                data-testid="resume-modal-btn"
              >
                {resumeModal?.button.copy}
              </Button>
            </div>
          )}
        </div>
      </div>
    </ResumeModalStyled>
  );
};

ResumeModal.displayName = "ResumeModal";

export default ResumeModal;

const ResumeModalBadge: FC<{
  backgroundColor?: string;
  borderColor?: string;
  icon?: string;
  iconColor?: string;
  copy?: string;
  copyColor?: string;
}> = ({ backgroundColor, borderColor, icon, iconColor, copy, copyColor }) => {
  const Icon = icon ? iconMap[icon] : null;
  return (
    <BadgeStyled
      backgroundColor={backgroundColor}
      borderColor={borderColor}
      copyColor={copyColor}
      iconColor={iconColor}
    >
      <div className="resume__badge__content">
        {Icon && (
          <Icon className="resume__badge__icon" fill={iconColor ? getColor(iconColor) : theme.colors.secondary.black} />
        )}
        {copy && <span className="resume__badge__copy" {...setHtml(copy)} />}
      </div>
    </BadgeStyled>
  );
};

ResumeModalBadge.displayName = "ResumeModalBadge";
